import { useNavigate } from 'react-router-dom';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { fetchData } from '../api';
import './SearchBar.css';

const SearchBar = ({ selectedTags, setSearchParams }) => {
  const [inputValue, setInputValue] = useState(selectedTags.join(' '));
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const wrapperRef = useRef(null);

  // Update input when selectedTags change externally
  useEffect(() => {
    setInputValue(selectedTags.join(','));
  }, [selectedTags]);

  // Debounced fetch of suggestions
  useEffect(() => {
    const timer = setTimeout(() => {
      const _query = inputValue.trim().split(',');
      const query = _query[_query.length - 1];
      if (query.length === 0) {
        setSuggestions([]);
        setShowSuggestions(false);
        setLoading(false);
        return;
      }
      setLoading(true);
      fetchData(`/api/tag/autocomplete/${encodeURIComponent(query)}/`)
        .then((data) => {
          // Assuming data is an array of tag strings
          setSuggestions(data["data"].map((t) => t["name"]));
          setShowSuggestions(true);
          setLoading(false);
        })
        .catch((err) => {
          console.error('Failed to fetch tag suggestions:', err);
          setSuggestions([]);
          setShowSuggestions(false);
          setLoading(false);
        });
    }, 300); // 300ms debounce

    return () => clearTimeout(timer);
  }, [inputValue]);

  // Handle click outside to close suggestions
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setShowSuggestions(false);
        setActiveIndex(-1);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    const tags = inputValue.trim().split(/\s+/).filter(t => t);
    //const tagsParam = tags.join(',');
    const tagsParam = tags.join(' ');
    navigate(`/search?tags=${tagsParam}`);
    setShowSuggestions(false);
  };

  const handleSuggestionClick = (suggestion) => {
    setInputValue(suggestion);
    setShowSuggestions(false);
    setActiveIndex(-1);
    // Optionally auto-submit after selection:
    // handleSubmit(new Event('submit'));
  };

  const handleKeyDown = (e) => {
    if (!showSuggestions || suggestions.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((prev) => (prev + 1) % suggestions.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((prev) => (prev - 1 + suggestions.length) % suggestions.length);
    } else if (e.key === 'Enter') {
      if (activeIndex >= 0 && activeIndex < suggestions.length) {
        e.preventDefault();
        handleSuggestionClick(suggestions[activeIndex]);
      }
    } else if (e.key === 'Escape') {
      setShowSuggestions(false);
      setActiveIndex(-1);
    }
  };

  return (
    <>
      <h3 className="search-title">Search</h3>
      <form className="search-bar" onSubmit={handleSubmit} ref={wrapperRef}>
        <input
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Search by tags (space-separated)"
          className="search-input"
          autoComplete="off"
        />
        <button type="submit" className="search-button">
          🔍
        </button>
        {showSuggestions && suggestions.length > 0 && (
          <ul className="suggestions-dropdown">
            {suggestions.map((tag, index) => (
              <li
                key={tag}
                className={index === activeIndex ? 'suggestion-active' : ''}
                onClick={() => handleSuggestionClick(tag)}
                onMouseEnter={() => setActiveIndex(index)}
              >
                {tag}
              </li>
            ))}
          </ul>
        )}
        {loading && <div className="suggestions-loading">Loading…</div>}
      </form>
    </>
  );
};

export default SearchBar;
